import React, { useCallback, useEffect, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { PackageResponse } from "../../app/models/responseModels/packageResponse";
import {
  PACKAGE_COLUMN_NAME,
  PACKAGE_FILTER_COLUMN_OPTIONS,
  PACKAGE_LABEL_NAME,
} from "./PackageConstants";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import AutoCompleteFilter from "../../components/common/autoCompleteFilter/AutoCompleteFilter";
import { DatePicker } from "@mui/x-date-pickers";
import { addDays } from "date-fns";

const COLUMN_WITH_DATE = [
  "receivedDate",
  "plannedDeliveryDate",
  "parcelArrivalDate",
  "deliveryDate",
];

const COLUMN_WITH_AUTOCOMPLETE = [
  "shipmentClientName",
  "parcelCenterLocationName",
  "fdpCompanyName",
  "serviceTypeName",
  "fdpId",
  "status",
  "parcelCenterId",
  "twoMan",
  "shipmentRecipientCity",
  "shipmentRecipientState",
  "shipmentRecipientZip",
];

type PackageDataFilterProps = {
  packageData: PackageResponse[];
  setFilteredData: React.Dispatch<React.SetStateAction<PackageResponse[]>>;
  filteredData: PackageResponse[];
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const PackageDataFilter = ({
  packageData,
  setFilteredData,
  filteredData,
}: PackageDataFilterProps) => {
  const [open, setOpen] = useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [trackingNumberFilter, setTrackingNumberFilter] = useState<string>("");

  const [clientNameOptions, setClientNameOptions] = useState<string[]>([]); //Dropdown Options
  const [clientNameSelectedOptions, setClientNameSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [parcelCenterOptions, setParcelCenterOptions] = useState<string[]>([]); //Dropdown Options
  const [parcelCenterSelectedOptions, setParcelCenterSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [fdpOptions, setFdpOptions] = useState<string[]>([]); //Dropdown Options
  const [fdpSelectedOptions, setFdpSelectedOptions] = useState<string[]>([]); //Dropdown selected value

  const [serviceTypeOptions, setServiceTypeOptions] = useState<string[]>([]); //Dropdown Options
  const [serviceTypeSelectedOptions, setServiceTypeSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [fdpIdOptions, setFdpIdOptions] = useState<string[]>([]); //Dropdown Options
  const [fdpIdSelectedOptions, setFdpIdSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [statusOptions, setStatusOptions] = useState<string[]>([]); //Dropdown Options
  const [statusSelectedOptions, setStatusSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [twoManOptions, setTwoManOptions] = useState<string[]>([]); //Dropdown Options
  const [twoManSelectedOptions, setTwoManSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [recipientCityOptions, setRecipientCityOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [recipientCitySelectedOptions, setRecipientCitySelectedOptions] =
    useState<string[]>([]); //Dropdown selected value
  const [recipientStateOptions, setRecipientStateOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [recipientStateSelectedOptions, setRecipientStateSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value
  const [recipientZipOptions, setRecipientZipOptions] = useState<string[]>([]); //Dropdown Options
  const [recipientZipSelectedOptions, setRecipientZipSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [manifestReceivedFromDate, setManifestReceivedFromDate] =
    useState<Date | null>(null);
  const [manifestReceivedToDate, setManifestReceivedToDate] =
    useState<Date | null>(null);

  const [plannedDeliveryFromDate, setPlannedDeliveryFromDate] =
    useState<Date | null>(null);
  const [plannedDeliveryToDate, setPlannedDeliveryToDate] =
    useState<Date | null>(null);

  const [actualArrivalFromDate, setActualArrivalFromDate] =
    useState<Date | null>(null);
  const [actualArrivalToDate, setActualArrivalToDate] = useState<Date | null>(
    null
  );
  const [scheduledDeliveryFromDate, setScheduledDeliveryFromDate] =
    useState<Date | null>(null);
  const [scheduledDeliveryToDate, setScheduledDeliveryToDate] =
    useState<Date | null>(null);

  const [packageVolumnFilter, setPackageVolumnFilter] = useState<string>("");
  const [packageWeightFilter, setPackageWeightFilter] = useState<string>("");

  const [recipientNameFilter, setRecipientNameFilter] = useState<string>("");
  const [recipientAddressLine1Filter, setRecipientAddressLine1Filter] =
    useState<string>("");
  const [recipientAddressLine2Filter, setRecipientAddressLine2Filter] =
    useState<string>("");
  // const [recipientCityFilter, setRecipientCityFilter] = useState<string>("");
  // const [recipientStateFilter, setRecipientStateFilter] = useState<string>("");
  // const [recipientZipFilter, setRecipientZipFilter] = useState<string>("");
  const [
    shipmentExternalPartnerShipmentIdFilter,
    setShipmentExternalPartnerShipmentIdFilter,
  ] = useState<string>("");

  const [parcelCenterIdOptions, setParcelCenterIdOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [parcelCenterIdSelectedOptions, setParcelCenterIdSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_CLIENT_NAME) {
        return clientNameOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        return parcelCenterOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.FDP_COMPANY_NAME) {
        return fdpOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SERVICE_TYPE_NAME) {
        return serviceTypeOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.FDP_ID) {
        return fdpIdOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.STATUS) {
        return statusOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_ID) {
        return parcelCenterIdOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.TWO_MAN) {
        return twoManOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_CITY) {
        return recipientCityOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_STATE) {
        return recipientStateOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ZIP) {
        return recipientZipOptions;
      }
      return [];
    },
    [
      clientNameOptions,
      parcelCenterOptions,
      fdpOptions,
      serviceTypeOptions,
      fdpIdOptions,
      statusOptions,
      parcelCenterIdOptions,
      twoManOptions,
      recipientCityOptions,
      recipientStateOptions,
      recipientZipOptions,
    ]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_CLIENT_NAME) {
        return clientNameSelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        return parcelCenterSelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.FDP_COMPANY_NAME) {
        return fdpSelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SERVICE_TYPE_NAME) {
        return serviceTypeSelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.FDP_ID) {
        return fdpIdSelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.STATUS) {
        return statusSelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_ID) {
        return parcelCenterIdSelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.TWO_MAN) {
        return twoManSelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_CITY) {
        return recipientCitySelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_STATE) {
        return recipientStateSelectedOptions;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ZIP) {
        return recipientZipSelectedOptions;
      }
      return [];
    },
    [
      clientNameSelectedOptions,
      parcelCenterSelectedOptions,
      fdpSelectedOptions,
      serviceTypeSelectedOptions,
      fdpIdSelectedOptions,
      statusSelectedOptions,
      parcelCenterIdSelectedOptions,
      twoManSelectedOptions,
      recipientCitySelectedOptions,
      recipientStateSelectedOptions,
      recipientZipSelectedOptions,
    ]
  );

  const getSelectedFromDate = useCallback(
    (columnName: string) => {
      if (columnName === PACKAGE_COLUMN_NAME.RECEIVED_DATE) {
        return manifestReceivedFromDate;
      }
      if (columnName === PACKAGE_COLUMN_NAME.PLANNED_DELIVERY_DATE) {
        return plannedDeliveryFromDate;
      }
      if (columnName === PACKAGE_COLUMN_NAME.PARCEL_ARRIVAL_DATE) {
        return actualArrivalFromDate;
      }
      if (columnName === PACKAGE_COLUMN_NAME.DELIVERY_DATE) {
        return scheduledDeliveryFromDate;
      }
      return null;
    },
    [
      manifestReceivedFromDate,
      plannedDeliveryFromDate,
      actualArrivalFromDate,
      scheduledDeliveryFromDate,
    ]
  );

  const getSelectedToDate = useCallback(
    (columnName: string) => {
      if (columnName === PACKAGE_COLUMN_NAME.RECEIVED_DATE) {
        return manifestReceivedToDate;
      }
      if (columnName === PACKAGE_COLUMN_NAME.PLANNED_DELIVERY_DATE) {
        return plannedDeliveryToDate;
      }
      if (columnName === PACKAGE_COLUMN_NAME.PARCEL_ARRIVAL_DATE) {
        return actualArrivalToDate;
      }
      if (columnName === PACKAGE_COLUMN_NAME.DELIVERY_DATE) {
        return scheduledDeliveryToDate;
      }
      return null;
    },
    [
      manifestReceivedToDate,
      plannedDeliveryToDate,
      actualArrivalToDate,
      scheduledDeliveryToDate,
    ]
  );

  const getSelectedTextFieldValue = useCallback(
    (columnName: string) => {
      if (columnName === PACKAGE_COLUMN_NAME.PACKAGE_VOLUME) {
        return packageVolumnFilter;
      }
      if (columnName === PACKAGE_COLUMN_NAME.PACKAGE_WEIGHT) {
        return packageWeightFilter;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_NAME) {
        return recipientNameFilter;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_1) {
        return recipientAddressLine1Filter;
      }
      if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_2) {
        return recipientAddressLine2Filter;
      }
      if (
        columnName === PACKAGE_COLUMN_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID
      ) {
        return shipmentExternalPartnerShipmentIdFilter;
      }
      return "";
    },
    [
      packageVolumnFilter,
      packageWeightFilter,
      recipientNameFilter,
      recipientAddressLine1Filter,
      recipientAddressLine2Filter,
      shipmentExternalPartnerShipmentIdFilter,
    ]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const selectedColumnName = inputList[0].columnName;
    //ClientName dropdown options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_CLIENT_NAME) {
      setClientNameOptions(
        packageData
          .map((v) => v.shipmentClientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setClientNameOptions(
        filteredData
          .map((v) => v.shipmentClientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Parcel Center dropdown options
    if (
      selectedColumnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME
    ) {
      setParcelCenterOptions(
        packageData
          .map((v) => v.parcelCenterLocationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setParcelCenterOptions(
        filteredData
          .map((v) => v.parcelCenterLocationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //FDP dropdown options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.FDP_COMPANY_NAME) {
      setFdpOptions(
        packageData
          .map((v) => v.fdpCompanyName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setFdpOptions(
        filteredData
          .map((v) => v.fdpCompanyName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //ServiceType dropdown options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.SERVICE_TYPE_NAME) {
      setServiceTypeOptions(
        packageData
          .map((v) => v.serviceTypeName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setServiceTypeOptions(
        filteredData
          .map((v) => v.serviceTypeName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Fdp Id dropdown options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.FDP_ID) {
      setFdpIdOptions(
        packageData
          .map((v) => v.fdpId)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setFdpIdOptions(
        filteredData
          .map((v) => v.fdpId)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Status dropdown options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.STATUS) {
      setStatusOptions(
        packageData
          .map((v) => v.status)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setStatusOptions(
        filteredData
          .map((v) => v.status)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //ParcelCenter Id dropdown options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_ID) {
      setParcelCenterIdOptions(
        packageData
          .map((v) => v.parcelCenterId)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setParcelCenterIdOptions(
        filteredData
          .map((v) => v.parcelCenterId)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    // Two Man fropdown options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.TWO_MAN) {
      setTwoManOptions(["Y", "N"]);
    }

    // Recipient City options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_CITY) {
      setRecipientCityOptions(
        packageData
          .map((v) => v.shipmentRecipientCity)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientCityOptions(
        filteredData
          .map((v) => v.shipmentRecipientCity)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    // Recipient State options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_STATE) {
      setRecipientStateOptions(
        packageData
          .map((v) => v.shipmentRecipientState)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientStateOptions(
        filteredData
          .map((v) => v.shipmentRecipientState)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    // Recipient Zip options
    if (selectedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ZIP) {
      setRecipientZipOptions(
        packageData
          .map((v) => v.shipmentRecipientZip)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientZipOptions(
        filteredData
          .map((v) => v.shipmentRecipientZip)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, packageData]);

  useEffect(() => {
    let newData = [...packageData];
    //ClientName Filter
    if (clientNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return clientNameSelectedOptions.some((s) => {
          return s === a.shipmentClientName;
        });
      });
    }

    //Parcel Center Filter
    if (parcelCenterSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return parcelCenterSelectedOptions.some((s) => {
          return s === a.parcelCenterLocationName;
        });
      });
    }

    //FDP Filter
    if (fdpSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return fdpSelectedOptions.some((s) => {
          return s === a.fdpCompanyName;
        });
      });
    }

    //Service Type Filter
    if (serviceTypeSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return serviceTypeSelectedOptions.some((s) => {
          return s === a.serviceTypeName;
        });
      });
    }

    //Fdp Id Filter
    if (fdpIdSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return fdpIdSelectedOptions.some((s) => {
          return s === a.fdpId;
        });
      });
    }

    //Status Filter
    if (statusSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return statusSelectedOptions.some((s) => {
          return s === a.status;
        });
      });
    }

    //Parcel Center Id Filter
    if (parcelCenterIdSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return parcelCenterIdSelectedOptions.some((s) => {
          return s === a.parcelCenterId;
        });
      });
    }

    //Two Man Filter
    if (twoManSelectedOptions.length > 0) {
      const twoManValues: number[] = [];
      if (twoManSelectedOptions.includes("Y")) {
        twoManValues.push(1);
      } else {
        twoManValues.push(0);
      }
      newData = newData.filter((a) => {
        return twoManValues.some((s) => {
          return s === a.twoMan;
        });
      });
    }

    //Recipient City Filter
    if (recipientCitySelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientCitySelectedOptions.some((s) => {
          return s === a.shipmentRecipientCity;
        });
      });
    }

    //Recipient State Filter
    if (recipientStateSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientStateSelectedOptions.some((s) => {
          return s === a.shipmentRecipientState;
        });
      });
    }

    //Recipient Zip Filter
    if (recipientZipSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientZipSelectedOptions.some((s) => {
          return s === a.shipmentRecipientZip;
        });
      });
    }

    //Tracking Filter
    if (trackingNumberFilter) {
      if (trackingNumberFilter.includes(",")) {
        const trackingNumbers = trackingNumberFilter.split(",");
        newData = newData.filter((a) => {
          return trackingNumbers.some((s) => {
            return s === a.trackingNumber;
          });
        });
      } else {
        newData = newData.filter((x) =>
          x.trackingNumber
            .toLowerCase()
            .includes(trackingNumberFilter.toLowerCase())
        );
      }
    }

    if (manifestReceivedFromDate && manifestReceivedToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.receivedDate).toLocaleDateString() >=
            manifestReceivedFromDate.toLocaleDateString() &&
          new Date(x.receivedDate).toLocaleDateString() <=
            manifestReceivedToDate.toLocaleDateString()
      );
    }

    if (plannedDeliveryFromDate && plannedDeliveryToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.plannedDeliveryDate).toLocaleDateString() >=
            plannedDeliveryFromDate.toLocaleDateString() &&
          new Date(x.plannedDeliveryDate).toLocaleDateString() <=
            plannedDeliveryToDate.toLocaleDateString()
      );
    }

    if (actualArrivalFromDate && actualArrivalToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.parcelArrivalDate).toLocaleDateString() >=
            actualArrivalFromDate.toLocaleDateString() &&
          new Date(x.parcelArrivalDate).toLocaleDateString() <=
            actualArrivalToDate.toLocaleDateString()
      );
    }

    if (scheduledDeliveryFromDate && scheduledDeliveryToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.deliveryDate).toLocaleDateString() >=
            scheduledDeliveryFromDate.toLocaleDateString() &&
          new Date(x.deliveryDate).toLocaleDateString() <=
            scheduledDeliveryToDate.toLocaleDateString()
      );
    }

    //Package Volumn Filter
    if (packageVolumnFilter) {
      newData = newData.filter((x) =>
        x.packageVolume.toString().includes(packageVolumnFilter)
      );
    }

    //Package Weight Filter
    if (packageWeightFilter) {
      newData = newData.filter((x) =>
        x.packageWeight.toString().includes(packageWeightFilter)
      );
    }

    //Recipient Name Filter
    if (recipientNameFilter) {
      newData = newData.filter((x) =>
        x.shipmentRecipientName
          .toLowerCase()
          .includes(recipientNameFilter.toLowerCase())
      );
    }

    //Recipient Address Line 1 Filter
    if (recipientAddressLine1Filter) {
      newData = newData.filter((x) =>
        x.shipmentRecipientAddressLine1
          .toLowerCase()
          .includes(recipientAddressLine1Filter.toLowerCase())
      );
    }

    //Recipient Address Line 2 Filter
    if (recipientAddressLine2Filter) {
      newData = newData.filter((x) =>
        x.shipmentRecipientAddressLine2
          .toLowerCase()
          .includes(recipientAddressLine2Filter.toLowerCase())
      );
    }

    //Shipment External Partner Shipment Id Filter
    if (shipmentExternalPartnerShipmentIdFilter) {
      newData = newData.filter((x) =>
        x.shipmentExternalPartnerShipmentId
          .toLowerCase()
          .includes(shipmentExternalPartnerShipmentIdFilter.toLowerCase())
      );
    }

    setFilteredData(newData);
  }, [
    packageData,
    setFilteredData,
    trackingNumberFilter,
    clientNameSelectedOptions,
    parcelCenterSelectedOptions,
    fdpSelectedOptions,
    serviceTypeSelectedOptions,
    fdpIdSelectedOptions,
    statusSelectedOptions,
    parcelCenterIdSelectedOptions,
    recipientCitySelectedOptions,
    recipientStateSelectedOptions,
    recipientZipSelectedOptions,
    manifestReceivedFromDate,
    manifestReceivedToDate,
    plannedDeliveryFromDate,
    plannedDeliveryToDate,
    actualArrivalFromDate,
    actualArrivalToDate,
    scheduledDeliveryFromDate,
    scheduledDeliveryToDate,
    packageVolumnFilter,
    packageWeightFilter,
    recipientNameFilter,
    recipientAddressLine1Filter,
    recipientAddressLine2Filter,
    shipmentExternalPartnerShipmentIdFilter,
    twoManSelectedOptions,
  ]);

  // ***** Handlers *****
  const handleTrackingNumberChange = (e: any) => {
    let trackingNumbers = e.target.value;
    if (
      trackingNumbers.includes(" ") ||
      trackingNumbers.includes("\t") ||
      trackingNumbers.includes(",")
    ) {
      setTrackingNumberFilter(
        trackingNumbers.split(" ").join(",").split("\t").join(",")
      );
    } else {
      setTrackingNumberFilter(trackingNumbers);
    }
  };

  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_CLIENT_NAME) {
      return setClientNameSelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      return setParcelCenterSelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.FDP_COMPANY_NAME) {
      return setFdpSelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.SERVICE_TYPE_NAME) {
      return setServiceTypeSelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.FDP_ID) {
      return setFdpIdSelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.STATUS) {
      return setStatusSelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_ID) {
      return setParcelCenterIdSelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_CITY) {
      setRecipientCitySelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_STATE) {
      setRecipientStateSelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ZIP) {
      setRecipientZipSelectedOptions([]);
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.RECEIVED_DATE) {
      setManifestReceivedFromDate(null);
      setManifestReceivedToDate(null);
      return;
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.PLANNED_DELIVERY_DATE) {
      setPlannedDeliveryFromDate(null);
      setPlannedDeliveryToDate(null);
      return;
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.PARCEL_ARRIVAL_DATE) {
      setActualArrivalFromDate(null);
      setActualArrivalToDate(null);
      return;
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.DELIVERY_DATE) {
      setScheduledDeliveryFromDate(null);
      setScheduledDeliveryToDate(null);
      return;
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.TWO_MAN) {
      setTwoManSelectedOptions([]);
      return;
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.PACKAGE_VOLUME) {
      setPackageVolumnFilter("");
      return;
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.PACKAGE_WEIGHT) {
      setPackageWeightFilter("");
      return;
    }
    if (removedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_NAME) {
      setRecipientNameFilter("");
    }
    if (
      removedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_1
    ) {
      setRecipientAddressLine1Filter("");
    }
    if (
      removedColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_2
    ) {
      setRecipientAddressLine2Filter("");
    }
    if (
      removedColumnName ===
      PACKAGE_COLUMN_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID
    ) {
      setShipmentExternalPartnerShipmentIdFilter("");
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];
    const oldColumnName = newInputList[index].columnName;

    if (oldColumnName.length > 0) {
      if (oldColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_CLIENT_NAME) {
        setClientNameSelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        setParcelCenterSelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.FDP_COMPANY_NAME) {
        setFdpSelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.SERVICE_TYPE_NAME) {
        setServiceTypeSelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.FDP_ID) {
        setFdpIdSelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.STATUS) {
        setStatusSelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_CITY) {
        setRecipientCitySelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_STATE) {
        setRecipientStateSelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ZIP) {
        setRecipientZipSelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.RECEIVED_DATE) {
        setManifestReceivedFromDate(null);
        setManifestReceivedToDate(null);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.PLANNED_DELIVERY_DATE) {
        setPlannedDeliveryFromDate(null);
        setPlannedDeliveryToDate(null);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.PARCEL_ARRIVAL_DATE) {
        setActualArrivalFromDate(null);
        setActualArrivalToDate(null);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.DELIVERY_DATE) {
        setScheduledDeliveryFromDate(null);
        setScheduledDeliveryToDate(null);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.PACKAGE_VOLUME) {
        setPackageVolumnFilter("");
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.PACKAGE_WEIGHT) {
        setPackageWeightFilter("");
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.TWO_MAN) {
        setTwoManSelectedOptions([]);
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_NAME) {
        setRecipientNameFilter("");
      }
      if (
        oldColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_1
      ) {
        setRecipientAddressLine1Filter("");
      }
      if (
        oldColumnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_2
      ) {
        setRecipientAddressLine2Filter("");
      }
      if (
        oldColumnName ===
        PACKAGE_COLUMN_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID
      ) {
        setShipmentExternalPartnerShipmentIdFilter("");
      }
      if (oldColumnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_ID) {
        setParcelCenterIdSelectedOptions([]);
      }
    }
    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_CLIENT_NAME) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_CLIENT_NAME;
    }
    if (value === PACKAGE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.PARCEL_CENTER_LOCATION_NAME;
    }
    if (value === PACKAGE_COLUMN_NAME.FDP_COMPANY_NAME) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.FDP_COMPANY_NAME;
    }
    if (value === PACKAGE_COLUMN_NAME.SERVICE_TYPE_NAME) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SERVICE_TYPE_NAME;
    }
    if (value === PACKAGE_COLUMN_NAME.FDP_ID) {
      newInputList[index].displayColumnName = PACKAGE_LABEL_NAME.FDP_ID;
    }
    if (value === PACKAGE_COLUMN_NAME.STATUS) {
      newInputList[index].displayColumnName = PACKAGE_LABEL_NAME.STATUS;
    }
    if (value === PACKAGE_COLUMN_NAME.TWO_MAN) {
      newInputList[index].displayColumnName = PACKAGE_LABEL_NAME.TWO_MAN;
    }

    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_CITY) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_CITY;
    }
    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_STATE) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_STATE;
    }
    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ZIP) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_ZIP;
    }
    if (value === PACKAGE_COLUMN_NAME.PARCEL_CENTER_ID) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.PARCEL_CENTER_ID;
    }
    if (value === PACKAGE_COLUMN_NAME.RECEIVED_DATE) {
      newInputList[index].displayColumnName = "Manifest Received";
    }
    if (value === PACKAGE_COLUMN_NAME.PLANNED_DELIVERY_DATE) {
      newInputList[index].displayColumnName = "Planned Delivery";
    }
    if (value === PACKAGE_COLUMN_NAME.PARCEL_ARRIVAL_DATE) {
      newInputList[index].displayColumnName = "Actual Arrival";
    }
    if (value === PACKAGE_COLUMN_NAME.DELIVERY_DATE) {
      newInputList[index].displayColumnName = "Delivery";
    }

    if (value === PACKAGE_COLUMN_NAME.PACKAGE_VOLUME) {
      newInputList[index].displayColumnName = PACKAGE_LABEL_NAME.PACKAGE_VOLUME;
    }
    if (value === PACKAGE_COLUMN_NAME.PACKAGE_WEIGHT) {
      newInputList[index].displayColumnName = PACKAGE_LABEL_NAME.PACKAGE_WEIGHT;
    }

    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_NAME) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_NAME;
    }
    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_1) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_1;
    }
    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_2) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_2;
    }
    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_CITY) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_CITY;
    }
    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_STATE) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_STATE;
    }
    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ZIP) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_ZIP;
    }
    if (value === PACKAGE_COLUMN_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID) {
      newInputList[index].displayColumnName =
        PACKAGE_LABEL_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID;
    }
    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleReset = (e: any) => {
    setTrackingNumberFilter("");
    setClientNameSelectedOptions([]);
    setParcelCenterSelectedOptions([]);
    setFdpSelectedOptions([]);
    setServiceTypeSelectedOptions([]);
    setFdpIdSelectedOptions([]);
    setStatusSelectedOptions([]);
    setRecipientCitySelectedOptions([]);
    setRecipientStateSelectedOptions([]);
    setRecipientZipSelectedOptions([]);
    setManifestReceivedFromDate(null);
    setManifestReceivedToDate(null);
    setPlannedDeliveryFromDate(null);
    setPlannedDeliveryToDate(null);
    setActualArrivalFromDate(null);
    setActualArrivalToDate(null);
    setScheduledDeliveryFromDate(null);
    setScheduledDeliveryToDate(null);
    setPackageVolumnFilter("");
    setPackageWeightFilter("");
    setTwoManSelectedOptions([]);
    setRecipientNameFilter("");
    setRecipientAddressLine1Filter("");
    setRecipientAddressLine2Filter("");
    setShipmentExternalPartnerShipmentIdFilter("");
    setParcelCenterIdSelectedOptions([]);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_CLIENT_NAME) {
      return setClientNameSelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      return setParcelCenterSelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.FDP_COMPANY_NAME) {
      return setFdpSelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.SERVICE_TYPE_NAME) {
      return setServiceTypeSelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.FDP_ID) {
      return setFdpIdSelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.STATUS) {
      return setStatusSelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.PARCEL_CENTER_ID) {
      return setParcelCenterIdSelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.TWO_MAN) {
      return setTwoManSelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_CITY) {
      return setRecipientCitySelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_STATE) {
      return setRecipientStateSelectedOptions(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ZIP) {
      return setRecipientZipSelectedOptions(value);
    }
  };

  const handleFromDateChange = (value: Date | null, columnName: string) => {
    if (columnName === PACKAGE_COLUMN_NAME.RECEIVED_DATE) {
      return setManifestReceivedFromDate(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.PLANNED_DELIVERY_DATE) {
      return setPlannedDeliveryFromDate(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.PARCEL_ARRIVAL_DATE) {
      return setActualArrivalFromDate(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.DELIVERY_DATE) {
      return setScheduledDeliveryFromDate(value);
    }
  };

  const handleToDateChange = (value: Date | null, columnName: string) => {
    if (columnName === PACKAGE_COLUMN_NAME.RECEIVED_DATE) {
      return setManifestReceivedToDate(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.PLANNED_DELIVERY_DATE) {
      return setPlannedDeliveryToDate(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.PARCEL_ARRIVAL_DATE) {
      return setActualArrivalToDate(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.DELIVERY_DATE) {
      return setScheduledDeliveryToDate(value);
    }
  };

  const handleTextFieldChange = (value: string, columnName: string) => {
    if (columnName === PACKAGE_COLUMN_NAME.PACKAGE_VOLUME) {
      return setPackageVolumnFilter(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.PACKAGE_WEIGHT) {
      return setPackageWeightFilter(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_NAME) {
      return setRecipientNameFilter(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_1) {
      return setRecipientAddressLine1Filter(value);
    }
    if (columnName === PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_2) {
      return setRecipientAddressLine2Filter(value);
    }
    if (
      columnName === PACKAGE_COLUMN_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID
    ) {
      return setShipmentExternalPartnerShipmentIdFilter(value);
    }
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={() => setOpen(!open)}
          expandIcon={
            <>
              <IconButton
                onClick={() => setOpen(!open)}
                sx={{ color: "white" }}
              >
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search Packages</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {PACKAGE_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  {COLUMN_WITH_DATE.includes(inputList[index].columnName) ? (
                    <Grid container rowSpacing={2} columnSpacing={2}>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        {/* <MuiDateRangePicker /> */}
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} From Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleFromDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedFromDate(
                            inputList[index].columnName
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} To Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleToDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedToDate(inputList[index].columnName)}
                        />
                      </Grid>
                    </Grid>
                  ) : COLUMN_WITH_AUTOCOMPLETE.includes(
                      inputList[index].columnName
                    ) ? (
                    <AutoCompleteFilter
                      columnName={inputList[index].columnName}
                      displayColumnName={inputList[index].displayColumnName}
                      options={getFilterOptions(inputList[index].columnName)}
                      value={getSelectedOptions(inputList[index].columnName)}
                      onChange={(event: any, selectedValue: string[]) => {
                        handleSelectedOption(
                          event,
                          inputList[index].columnName,
                          selectedValue
                        );
                      }}
                    />
                  ) : (
                    <TextField
                      margin="normal"
                      fullWidth
                      label={`${inputList[index].displayColumnName}`}
                      size="small"
                      onChange={(event) =>
                        handleTextFieldChange(
                          event.target.value,
                          inputList[index].columnName
                        )
                      }
                      value={getSelectedTextFieldValue(
                        inputList[index].columnName
                      )}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
            {/* Tracking Number Filter */}
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  key={"trackingNumber"}
                  margin="normal"
                  id="trackingNumber"
                  label="Tracking Number"
                  name="trackingNumber"
                  size="small"
                  fullWidth
                  value={trackingNumberFilter}
                  onChange={handleTrackingNumberChange}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default PackageDataFilter;
